import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
    Container,
    Stack,
    Loader,
    Paper,
    Text,
    Box,
    useMantineTheme,
    ActionIcon,
    Collapse,
    Card,
    Group,
    Badge,
    ThemeIcon,
    Button,
    Textarea,
    NumberInput,
    Grid,
    Alert,
    Divider,
    Center
} from '@mantine/core';
import {
    IconCheck,
    IconPackages,
    IconX,
    IconChevronDown,
    IconChevronUp,
    IconWeight,
    IconRuler,
    IconCoin,
    IconAlertCircle,
    IconPackage,
    IconArrowLeft
} from '@tabler/icons-react';
import { useMediaQuery, useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import PageTitle from '../components/common/PageTitle';
import { PackageService } from '../types/services/PackageService';
import { Package } from '../components/package/PackageList';
import { CombinedPackageService } from '../types/services/CombinedPackageService';

// Import new components
import SuccessScreen from '../components/package/SuccessScreen';

// Form types
interface ConsolidateForm {
    description: string;
    customValue: number;
}

const ConsolidatePackages = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useMantineTheme();
    const isMobile = useMediaQuery('(max-width: 768px)');

    // Component state
    const [selectedPackages, setSelectedPackages] = useState<Package[]>([]);
    const [loading, setLoading] = useState(true);
    const [consolidating, setConsolidating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [combinedPackageResponse, setCombinedPackageResponse] = useState<any>(null);
    const [expandedPackages, setExpandedPackages] = useState<Set<number>>(new Set());
    const [detailsExpanded, { toggle: toggleDetails }] = useDisclosure(false);

    // React Hook Form
    const {
        control,
        handleSubmit,
        setValue,
        getValues,
        formState: { errors }
    } = useForm<ConsolidateForm>({
        mode: 'onBlur', // Changed from 'onChange' to 'onBlur' to reduce re-renders
        defaultValues: {
            description: '',
            customValue: 0
        }
    });

    // Responsive sizing
    const containerSize = isMobile ? 'sm' : 'md';
    const paperPadding = isMobile ? 'sm' : 'md';
    const stackGap = isMobile ? 'sm' : 'md';
    const buttonSize = isMobile ? 'sm' : 'md';
    const fontSize = isMobile ? 'sm' : 'md';

    // Extract package IDs from URL
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const packageIdsParam = queryParams.get('packageIds');

        if (!packageIdsParam) {
            setError('No packages selected for consolidation.');
            setLoading(false);
            return;
        }

        const packageIds = packageIdsParam.split(',');
        if (packageIds.length < 2) {
            setError('You need at least 2 packages to consolidate.');
            setLoading(false);
            return;
        }

        fetchPackageDetails(packageIds);
    }, [location]);

    // Fetch all package details
    const fetchPackageDetails = async (packageIds: string[]) => {
        try {
            setLoading(true);
            const packagePromises = packageIds.map(id => PackageService.getPackage1(parseInt(id, 10)));
            const packageResults = await Promise.all(packagePromises);

            // Format packages with consistent properties
            const formattedPackages = packageResults.map((pkg: any) => ({
                ...pkg,
                type: 'regular',
                status: pkg.shipment?.state === 'APPROVED' || pkg.shipment?.state === 'REQUESTED' ? 'SHIPPED' : 'RECEIVED',
                receivedDate: pkg.created_at || new Date().toISOString(),
            }));

            // Validate if packages can be consolidated
            const invalidPackages = formattedPackages.filter(pkg =>
                pkg.status === 'SHIPPED' ||
                (pkg.shipment && (pkg.shipment.state === 'APPROVED' || pkg.shipment.state === 'REQUESTED'))
            );

            if (invalidPackages.length > 0) {
                setError('Some selected packages have already been shipped or are in shipping process.');
                setLoading(false);
                return;
            }

            setSelectedPackages(formattedPackages);

            // Set initial values for combined package
            const totalValue = formattedPackages.reduce(
                (sum, pkg) => sum + (parseFloat(pkg.custom_value || '0') || 0),
                0
            );
            setValue('customValue', totalValue);

            // Generate a default description
            setValue('description', `Consolidated package (${formattedPackages.length} items)`);

        } catch (error) {
            setError('Failed to load package details. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Calculate total weight and dimensions
    const calculateTotals = () => {
        if (!selectedPackages.length) return { weight: 0, volume: 0 };

        const totalWeight = selectedPackages.reduce(
            (sum, pkg) => sum + (parseFloat(pkg.weight) || 0),
            0
        );

        // Calculate volume for each package and sum
        const totalVolume = selectedPackages.reduce((sum, pkg: any) => {
            const length = parseFloat(pkg.length) || 0;
            const width = parseFloat(pkg.width) || 0;
            const height = parseFloat(pkg.height) || 0;
            return sum + (length * width * height);
        }, 0);

        return { weight: totalWeight, volume: totalVolume };
    };

    // Calculate suggested dimensions based on total volume
    const calculateSuggestedDimensions = () => {
        const { volume } = calculateTotals();
        if (volume <= 0) return { length: 0, width: 0, height: 0 };

        // Cube root to get equal dimensions as a starting point
        const dimension = Math.ceil(Math.pow(volume, 1 / 3));

        return {
            length: dimension,
            width: dimension,
            height: dimension
        };
    };

    // Handle removing a package from the selection
    const handleRemovePackage = (packageId: number) => {
        // Ensure we'll still have at least 2 packages after removal
        if (selectedPackages.length <= 2) {
            notifications.show({
                title: 'Cannot Remove Package',
                message: 'You need at least 2 packages for consolidation.',
                color: 'red',
            });
            return;
        }

        // Remove the package from the selection
        const updatedPackages = selectedPackages.filter(pkg => pkg.id !== packageId);
        setSelectedPackages(updatedPackages);

        // Update the URL to reflect the change
        const newPackageIds = updatedPackages.map(pkg => pkg.id).join(',');
        navigate(`/consolidate?packageIds=${newPackageIds}`, { replace: true });

        // Recalculate custom value
        const newTotalValue = updatedPackages.reduce(
            (sum, pkg: any) => sum + (parseFloat(pkg.custom_value || '0') || 0),
            0
        );
        setValue('customValue', newTotalValue);

        // Update description
        setValue('description', `Consolidated package (${updatedPackages.length} items)`);

        notifications.show({
            title: 'Package Removed',
            message: 'Package has been removed from consolidation.',
            color: 'blue',
        });
    };

    // Toggle package expansion
    const togglePackageExpanded = (packageId: number) => {
        setExpandedPackages(prev => {
            const newSet = new Set(prev);
            if (newSet.has(packageId)) {
                newSet.delete(packageId);
            } else {
                newSet.add(packageId);
            }
            return newSet;
        });
    };

    // Handle consolidation
    const onSubmit = async (_formData: ConsolidateForm) => {
        try {
            setConsolidating(true);
            setError(null);

            // Prepare data for API call
            const packageIds = selectedPackages.map(pkg => pkg.id);

            // Call consolidation API with correct parameters
            const response = await CombinedPackageService.postPackageCombineRequest({
                packageIds: packageIds,
            });
            setCombinedPackageResponse(response);

            setSuccess(true);
            notifications.show({
                title: 'Packages Consolidated Successfully',
                message: 'Your packages have been combined into one shipment.',
                color: 'green',
                icon: <IconCheck size="1.1rem" />,
            });

        } catch (error) {
            setError('Failed to consolidate packages. Please try again.');
        } finally {
            setConsolidating(false);
        }
    };

    const { weight } = calculateTotals();
    const dimensions = calculateSuggestedDimensions();

    // Mobile-optimized Error Display Component
    const ErrorDisplay = () => (
        <Container size={containerSize} py={isMobile ? "md" : "xl"}>
            <Alert
                icon={<IconAlertCircle size={isMobile ? 16 : 20} />}
                title="Error"
                color="red"
                radius="md"
                mb="md"
            >
                <Text size={fontSize}>{error}</Text>
            </Alert>
            <Center>
                <Button
                    leftSection={<IconArrowLeft size={isMobile ? 14 : 16} />}
                    onClick={() => navigate('/packages')}
                    variant="outline"
                    size={buttonSize}
                >
                    Back to Packages
                </Button>
            </Center>
        </Container>
    );

    // Mobile-optimized Selected Packages List Component
    const SelectedPackagesList = () => (
        <Card withBorder radius="md" p={paperPadding}>
            <Group justify="space-between" mb={stackGap}>
                <Group gap="xs">
                    <ThemeIcon size={isMobile ? "md" : "lg"} variant="light" color="blue">
                        <IconPackages size={isMobile ? 16 : 20} />
                    </ThemeIcon>
                    <Text fw={600} size={fontSize}>
                        Selected Packages
                    </Text>
                </Group>
                <Badge variant="light" color="blue" size={isMobile ? "sm" : "md"}>
                    {selectedPackages.length} packages
                </Badge>
            </Group>

            <Stack gap={isMobile ? "xs" : "sm"}>
                {selectedPackages.map((pkg: any) => (
                    <Paper key={pkg.id} withBorder p={isMobile ? "xs" : "sm"} radius="sm">
                        <Group justify="space-between" wrap="nowrap">
                            <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
                                <ThemeIcon size={isMobile ? "sm" : "md"} variant="light" color="blue">
                                    <IconPackage size={isMobile ? 12 : 16} />
                                </ThemeIcon>
                                <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                                    <Text size={isMobile ? "xs" : "sm"} fw={500} lineClamp={1}>
                                        Package #{pkg.id}
                                    </Text>
                                    <Text size="xs" c="dimmed" lineClamp={1}>
                                        {pkg.description || 'No description'}
                                    </Text>
                                </Stack>
                            </Group>
                            <Group gap={isMobile ? 4 : 8}>
                                <ActionIcon
                                    variant="subtle"
                                    size={isMobile ? "sm" : "md"}
                                    onClick={() => togglePackageExpanded(pkg.id)}
                                >
                                    {expandedPackages.has(pkg.id) ? (
                                        <IconChevronUp size={14} />
                                    ) : (
                                        <IconChevronDown size={14} />
                                    )}
                                </ActionIcon>
                                {selectedPackages.length > 2 && (
                                    <ActionIcon
                                        variant="subtle"
                                        color="red"
                                        size={isMobile ? "sm" : "md"}
                                        onClick={() => handleRemovePackage(pkg.id)}
                                    >
                                        <IconX size={isMobile ? 12 : 14} />
                                    </ActionIcon>
                                )}
                            </Group>
                        </Group>

                        <Collapse in={expandedPackages.has(pkg.id)}>
                            <Divider my={isMobile ? "xs" : "sm"} />
                            <Grid gutter={isMobile ? "xs" : "sm"}>
                                <Grid.Col span={6}>
                                    <Group gap="xs">
                                        <IconWeight size={12} color={theme.colors.gray[6]} />
                                        <Text size="xs" c="dimmed">
                                            Weight: {pkg.weight || 'N/A'}
                                        </Text>
                                    </Group>
                                </Grid.Col>
                                <Grid.Col span={6}>
                                    <Group gap="xs">
                                        <IconRuler size={12} color={theme.colors.gray[6]} />
                                        <Text size="xs" c="dimmed">
                                            {pkg.length}×{pkg.width}×{pkg.height}
                                        </Text>
                                    </Group>
                                </Grid.Col>
                                <Grid.Col span={6}>
                                    <Group gap="xs">
                                        <IconCoin size={12} color={theme.colors.gray[6]} />
                                        <Text size="xs" c="dimmed">
                                            Value: ${pkg.custom_value || '0.00'}
                                        </Text>
                                    </Group>
                                </Grid.Col>
                                <Grid.Col span={6}>
                                    <Badge size="xs" variant="light" color={pkg.status === 'RECEIVED' ? 'green' : 'yellow'}>
                                        {pkg.status}
                                    </Badge>
                                </Grid.Col>
                            </Grid>
                        </Collapse>
                    </Paper>
                ))}
            </Stack>
        </Card>
    );

    // Mobile-optimized Consolidation Summary Component
    const ConsolidationSummary = () => {
        const customValue = getValues('customValue');

        return (
            <Box>
                <Group justify="space-between" mb={stackGap}>
                    <Text fw={600} size={fontSize}>Consolidation Summary</Text>
                    <ActionIcon
                        variant="subtle"
                        onClick={toggleDetails}
                        size={isMobile ? "sm" : "md"}
                    >
                        {detailsExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                    </ActionIcon>
                </Group>

                <Grid gutter={isMobile ? "xs" : "sm"} mb={stackGap}>
                    <Grid.Col span={isMobile ? 6 : 4}>
                        <Group gap="xs">
                            <ThemeIcon color="blue" variant="light" size={isMobile ? "sm" : "md"} radius="xl">
                                <IconWeight size={isMobile ? 12 : 16} />
                            </ThemeIcon>
                            <Box>
                                <Text c="dimmed" size="xs">Total Weight</Text>
                                <Text size={isMobile ? "sm" : "md"} fw={500}>
                                    {weight.toFixed(2)} kg
                                </Text>
                            </Box>
                        </Group>
                    </Grid.Col>
                    <Grid.Col span={isMobile ? 6 : 4}>
                        <Group gap="xs">
                            <ThemeIcon color="green" variant="light" size={isMobile ? "sm" : "md"} radius="xl">
                                <IconCoin size={isMobile ? 12 : 16} />
                            </ThemeIcon>
                            <Box>
                                <Text c="dimmed" size="xs">Total Value</Text>
                                <Text size={isMobile ? "sm" : "md"} fw={500}>
                                    ${customValue?.toFixed(2) || '0.00'}
                                </Text>
                            </Box>
                        </Group>
                    </Grid.Col>
                    {!isMobile && (
                        <Grid.Col span={4}>
                            <Group gap="xs">
                                <ThemeIcon color="orange" variant="light" size="md" radius="xl">
                                    <IconPackages size={16} />
                                </ThemeIcon>
                                <Box>
                                    <Text c="dimmed" size="xs">Packages</Text>
                                    <Text size="md" fw={500}>
                                        {selectedPackages.length} items
                                    </Text>
                                </Box>
                            </Group>
                        </Grid.Col>
                    )}
                </Grid>

                <Collapse in={detailsExpanded}>
                    <Divider mb={stackGap} />
                    <Text size={isMobile ? "xs" : "sm"} c="dimmed" mb="xs">
                        Suggested dimensions (based on volume):
                    </Text>
                    <Text size={isMobile ? "xs" : "sm"} c="dimmed">
                        {dimensions.length} × {dimensions.width} × {dimensions.height} cm
                    </Text>
                </Collapse>
            </Box>
        );
    };

    // Mobile-optimized Package Details Form Component
    const PackageDetailsForm = () => (
        <Box>
            <Divider my={stackGap} />

            <form onSubmit={handleSubmit(onSubmit)}>
                <Stack gap={stackGap}>
                    <Box>
                        <Text fw={500} size={fontSize} mb={isMobile ? "xs" : "sm"}>
                            Package Description *
                        </Text>
                        <Controller
                            name="description"
                            control={control}
                            rules={{
                                required: 'Package description is required',
                                minLength: {
                                    value: 3,
                                    message: 'Description must be at least 3 characters long'
                                }
                            }}
                            render={({ field }) => (
                                <Textarea
                                    {...field}
                                    placeholder="Describe the consolidated package..."
                                    minRows={isMobile ? 2 : 3}
                                    size={buttonSize}
                                    radius="md"
                                    error={errors.description?.message}
                                />
                            )}
                        />
                    </Box>

                    <Box>
                        <Text fw={500} size={fontSize} mb={isMobile ? "xs" : "sm"}>
                            Declared Value (USD) *
                        </Text>
                        <Controller
                            name="customValue"
                            control={control}
                            rules={{
                                required: 'Declared value is required',
                                min: {
                                    value: 0.01,
                                    message: 'Value must be greater than 0'
                                }
                            }}
                            render={({ field }) => (
                                <NumberInput
                                    value={field.value}
                                    onChange={val => field.onChange(val === '' ? 0 : Number(val))}
                                    placeholder="Enter total value"
                                    min={0}
                                    step={0.01}
                                    decimalScale={2}
                                    prefix="$"
                                    size={buttonSize}
                                    radius="md"
                                    error={errors.customValue?.message}
                                />
                            )}
                        />
                    </Box>

                    <Group
                        gap={isMobile ? "xs" : "sm"}
                        mt={stackGap}
                        style={isMobile ? { flexDirection: 'column' } : {}}
                    >
                        <Button
                            variant="outline"
                            onClick={() => navigate('/packages')}
                            size={buttonSize}
                            style={isMobile ? { width: '100%' } : {}}
                            leftSection={<IconArrowLeft size={isMobile ? 14 : 16} />}
                            type="button"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            loading={consolidating}
                            size={buttonSize}
                            style={isMobile ? { width: '100%' } : {}}
                            leftSection={<IconPackages size={isMobile ? 14 : 16} />}
                        >
                            {consolidating ? 'Processing...' : 'Consolidate Packages'}
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Box>
    );

    // If consolidation successful, show success screen
    if (success) {
        return <SuccessScreen
            packageCount={selectedPackages.length}
            navigate={navigate}
            responseData={combinedPackageResponse}
            totalWeight={weight}
            customValue={getValues('customValue')}
        />;
    }

    return (
        <Container size={containerSize} py={isMobile ? "md" : "xl"}>
            <PageTitle
                title="Consolidate Packages"
                description={isMobile
                    ? "Combine packages to save shipping costs"
                    : "Combine multiple packages into one shipment to save on shipping costs"
                }
                icon={<IconPackages size={isMobile ? 20 : 24} />}
                breadcrumbs={[
                    { title: 'Dashboard', href: '/dashboard' },
                    { title: 'Packages', href: '/packages' },
                    { title: 'Consolidate Packages' }
                ]}
                withBorder
                compact={isMobile}
                size={isMobile ? "sm" : "md"}
            />

            {loading ? (
                <Stack align="center" py={isMobile ? "md" : "xl"}>
                    <Loader size={isMobile ? "md" : "lg"} />
                    <Text size={fontSize} c="dimmed">Loading packages...</Text>
                </Stack>
            ) : error ? (
                <ErrorDisplay />
            ) : (
                <Stack gap={stackGap}>
                    <SelectedPackagesList />

                    <Paper p={paperPadding} radius="md" withBorder>
                        <ConsolidationSummary />
                        <PackageDetailsForm />
                    </Paper>
                </Stack>
            )}
        </Container>
    );
};

export default ConsolidatePackages;
